import {
  FileCode2,
  X as XIcon,
  XSquare,
  ArrowRightSquare,
  ArrowDownSquare,
} from 'lucide-react';
import { findPaneForTab, useWorkspaceStore } from '@/stores/workspace-store';
import { useConnectionStore } from '@/stores/connection-store';
import { useToastStore } from '@/stores/toast-store';
import type { Command } from '../types';

function openNewQueryTab(): void {
  const { activeConnections } = useConnectionStore.getState();
  const connectedId = Object.keys(activeConnections).find(
    (id) => activeConnections[id].status === 'connected',
  );
  if (!connectedId) {
    useToastStore.getState().addToast(
      'warning',
      'No active connection. Connect to a database first.',
    );
    return;
  }
  useWorkspaceStore.getState().openTab({
    type: 'query',
    id: `query-${Date.now()}`,
    connectionId: connectedId,
    title: 'New Query',
    sql: '',
  });
}

/**
 * Tab and workspace commands. Most have `when()` guards tied to the focused
 * tab — "Close Other Tabs" is pointless when there's only one tab, etc.
 */
export const tabCommands: Command[] = [
  {
    id: 'query.new',
    label: 'New Query Tab',
    icon: FileCode2,
    category: 'Query',
    shortcut: 'Ctrl+T',
    keywords: ['sql', 'editor'],
    run: () => openNewQueryTab(),
  },
  {
    id: 'tab.close',
    label: 'Close Tab',
    icon: XIcon,
    category: 'Tabs',
    shortcut: 'Ctrl+W',
    when: (ctx) => ctx.activeTab !== null,
    run: (ctx) => {
      if (ctx.activeTab) useWorkspaceStore.getState().closeTab(ctx.activeTab.id);
    },
  },
  {
    id: 'tab.close-others',
    label: 'Close Other Tabs',
    icon: XSquare,
    category: 'Tabs',
    when: (ctx) => ctx.openTabs.length > 1 && ctx.activeTab !== null,
    run: (ctx) => {
      const keep = ctx.activeTab?.id;
      if (!keep) return;
      const ws = useWorkspaceStore.getState();
      const toClose = ctx.openTabs.filter((t) => t.id !== keep);
      for (const t of toClose) ws.closeTab(t.id);
    },
  },
  {
    id: 'tab.close-all',
    label: 'Close All Tabs',
    icon: XSquare,
    category: 'Tabs',
    when: (ctx) => ctx.openTabs.length > 0,
    run: (ctx) => {
      const ws = useWorkspaceStore.getState();
      for (const t of ctx.openTabs) ws.closeTab(t.id);
    },
  },
  {
    id: 'tab.switch',
    label: 'Switch to Tab...',
    icon: ArrowRightSquare,
    category: 'Tabs',
    shortcut: 'Ctrl+Tab',
    keywords: ['goto', 'jump'],
    when: (ctx) => ctx.openTabs.length > 1,
    // Switching to a specific tab is done via the search mode's `@tab` scope —
    // this command just nudges the user toward it.
    run: () => {
      window.dispatchEvent(new CustomEvent('dbgo:command-bar:open', {
        detail: { mode: 'search', prefill: '@tab ' },
      }));
    },
  },
  {
    id: 'pane.split-right',
    label: 'Split Pane Right',
    icon: ArrowRightSquare,
    category: 'Panes',
    keywords: ['horizontal', 'side by side'],
    when: (ctx) => ctx.activeTab !== null,
    run: (ctx) => {
      if (!ctx.activeTab) return;
      const ws = useWorkspaceStore.getState();
      const pane = findPaneForTab(ws.root, ctx.activeTab.id);
      if (pane) ws.splitPane(pane.id, 'horizontal', ctx.activeTab.id);
    },
  },
  {
    id: 'pane.split-down',
    label: 'Split Pane Down',
    icon: ArrowDownSquare,
    category: 'Panes',
    keywords: ['vertical', 'stack'],
    when: (ctx) => ctx.activeTab !== null,
    run: (ctx) => {
      if (!ctx.activeTab) return;
      const ws = useWorkspaceStore.getState();
      const pane = findPaneForTab(ws.root, ctx.activeTab.id);
      if (pane) ws.splitPane(pane.id, 'vertical', ctx.activeTab.id);
    },
  },
];
